import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { LoadingSpinner, NavigationBar, PageContainer } from '../../../shared/ui';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import { performYowanLogout } from './yowanAuthAdapter';
import { useYowanAuth } from './YowanAuthContext';
import { fetchYowanAdminMailSummary, fetchYowanPublicSitePageSlots } from './yowanContentClient';

const HEADER_LOGO_SLOT_KEY = 'header_logo';

const isPublishedHeaderLogoSlot = (slot = {}) =>
  slot.isPublished !== false && slot.is_published !== false;

const selectHeaderLogoSlot = (slots = []) =>
  slots.find(
    (slot) =>
      isPublishedHeaderLogoSlot(slot)
      && (slot?.slotKey === HEADER_LOGO_SLOT_KEY || slot?.slot_key === HEADER_LOGO_SLOT_KEY),
  ) || null;

const getHeaderLogoImageSrc = (image = null) => {
  if (!image) {
    return '';
  }

  return image.url || image.preview_url || image.previewUrl || '';
};

const getUnreadBadgeLabel = (value) => {
  const count = Number(value || 0);

  if (!Number.isFinite(count) || count <= 0) {
    return '';
  }

  return count > 99 ? '99+' : String(count);
};

const hasActiveStakeholderAccess = (access = null) => {
  if (!access) {
    return false;
  }

  if (Array.isArray(access)) {
    return access.some(hasActiveStakeholderAccess);
  }

  if (typeof access !== 'object') {
    return Boolean(access);
  }

  if (Array.isArray(access.items)) {
    return access.items.some(hasActiveStakeholderAccess);
  }

  if (Array.isArray(access.results)) {
    return access.results.some(hasActiveStakeholderAccess);
  }

  return access.isActive === true || access.is_active === true;
};

const YowanHeader = ({ navigationItems, floatingMenuActive = false }) => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [headerLogoSlot, setHeaderLogoSlot] = useState(null);
  const [isHeaderLogoLoading, setIsHeaderLogoLoading] = useState(true);
  const [adminMailSummary, setAdminMailSummary] = useState(null);
  const menuRegionRef = useRef(null);
  const isAdminRoute = pathname.startsWith('/admin');
  const { auth, refreshAuth } = useYowanAuth();
  const currentUser = auth.currentUser;
  const canAccessAdminMail = Boolean(currentUser.canAccessAdmin);
  const adminMailUnreadLabel = getUnreadBadgeLabel(adminMailSummary?.unreadCount);
  const myPageAriaLabel = adminMailUnreadLabel
    ? `マイページ。管理メール未読${adminMailSummary.unreadCount}件`
    : 'マイページ';
  const canShowStakeholderUtility = Boolean(
    currentUser.canAccessRelatedSeminars ||
    currentUser.canAccessRelatedClasses ||
    currentUser.isInstructor ||
    currentUser.isOrganizer ||
    hasActiveStakeholderAccess(currentUser.instructorAccess) ||
    hasActiveStakeholderAccess(currentUser.organizerAccess)
  );
  const authPath = currentUser.isAuthenticated ? '/' : '/login';
  const authLabel = currentUser.isAuthenticated ? 'Logout' : 'Login';
  const isStakeholderRoute = pathname.startsWith('/stakeholder');
  const isBlogAuthorRoute = pathname.startsWith('/blog-author');
  const shouldForceMenu = isAdminRoute || isStakeholderRoute || isBlogAuthorRoute || floatingMenuActive;

  const canUseHover = () => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return false;
    }
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  };

  useEffect(() => {
    if (typeof document === 'undefined' || !menuOpen) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (!menuRegionRef.current?.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [menuOpen]);

  useEffect(() => {
    let isMounted = true;

    setIsHeaderLogoLoading(true);
    fetchYowanPublicSitePageSlots()
      .then(({ results }) => {
        if (isMounted) {
          setHeaderLogoSlot(selectHeaderLogoSlot(results));
        }
      })
      .catch(() => {
        if (isMounted) {
          setHeaderLogoSlot(null);
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsHeaderLogoLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!canAccessAdminMail) {
      setAdminMailSummary(null);
      return undefined;
    }

    let isMounted = true;

    fetchYowanAdminMailSummary()
      .then((summary) => {
        if (isMounted) {
          setAdminMailSummary(summary);
        }
      })
      .catch(() => {
        if (isMounted) {
          setAdminMailSummary(null);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [canAccessAdminMail, pathname]);

  const headerLogoImage = headerLogoSlot?.primary_image || headerLogoSlot?.primaryImage || null;
  const headerLogoSrc = getHeaderLogoImageSrc(headerLogoImage);

  const brand = (
    <Link
      className="yowan-shell__brand"
      to={appendYowanPreviewQueryIfNeeded('/', search)}
      aria-label="揺腕法"
    >
      <span
        className={`yowan-shell__brand-mark${headerLogoSrc ? ' yowan-shell__brand-mark--image' : ''}`}
        aria-hidden="true"
      >
        {headerLogoSrc ? (
          <img
            className="yowan-shell__brand-logo"
            src={headerLogoSrc}
            alt=""
            loading="eager"
            decoding="async"
          />
        ) : isHeaderLogoLoading ? (
          <LoadingSpinner className="yowan-shell__brand-loading" size="sm" />
        ) : (
          <span className="yowan-shell__brand-fallback-label">logo</span>
        )}
      </span>
      <span className="yowan-shell__brand-label">揺腕法</span>
    </Link>
  );

  const orderedNavigationItems = [...navigationItems].sort((a, b) => {
    const order = {
      '/': 0,
      '/classes': 1,
      '/founder': 2,
      '/reviews': 3,
      '/blog': 4,
      '/mail-magazine': 5,
      '/contact': 6,
    };

    return (order[a.path] ?? 999) - (order[b.path] ?? 999);
  });

  const navItems = orderedNavigationItems.map((item) => (
      <Link
        key={item.path}
        className="yowan-shell__nav-link"
        to={appendYowanPreviewQueryIfNeeded(item.path, search)}
        onClick={() => setMenuOpen(false)}
      >
        {item.navLabel}
      </Link>
    ));

  const items = [
    ...navItems,
      <Link
        key="auth-link"
        className="yowan-shell__nav-link yowan-shell__nav-link--auth"
        to={appendYowanPreviewQueryIfNeeded(authPath, search)}
        onClick={async (event) => {
          if (currentUser.isAuthenticated) {
            event.preventDefault();
            await performYowanLogout();
            refreshAuth();
            setMenuOpen(false);
            navigate(appendYowanPreviewQueryIfNeeded('/', search));
            return;
          }
          setMenuOpen(false);
        }}
      >
        {authLabel}
      </Link>,
  ];

  const utilityLinks = currentUser.isAuthenticated ? (
    <div className="yowan-shell__header-utilities">
      <Link
        className="yowan-shell__utility-link"
        to={appendYowanPreviewQueryIfNeeded('/mypage', search)}
        aria-label={myPageAriaLabel}
      >
        <span className="yowan-shell__utility-icon yowan-shell__utility-icon--mypage" aria-hidden="true" />
        {adminMailUnreadLabel ? (
          <span className="yowan-shell__utility-badge" aria-hidden="true">
            {adminMailUnreadLabel}
          </span>
        ) : null}
      </Link>
      {canShowStakeholderUtility ? (
        <Link
          className="yowan-shell__utility-link"
          to={appendYowanPreviewQueryIfNeeded('/stakeholder', search)}
          aria-label="関係者ページ"
          aria-current={isStakeholderRoute ? 'page' : undefined}
        >
          <span className="yowan-shell__utility-icon yowan-shell__utility-icon--stakeholder" aria-hidden="true" />
        </Link>
      ) : null}
      {currentUser.canAccessBlogAuthor ? (
        <Link
          className="yowan-shell__utility-link"
          to={appendYowanPreviewQueryIfNeeded('/blog-author', search)}
          aria-label="ブログ執筆者ページ"
          aria-current={isBlogAuthorRoute ? 'page' : undefined}
        >
          <span className="yowan-shell__utility-icon yowan-shell__utility-icon--stakeholder" aria-hidden="true" />
        </Link>
      ) : null}
      {currentUser.canAccessAdmin ? (
        <Link
          className="yowan-shell__utility-link"
          to={appendYowanPreviewQueryIfNeeded('/admin', search)}
          aria-label="管理画面"
        >
          <span className="yowan-shell__utility-icon yowan-shell__utility-icon--admin" aria-hidden="true" />
        </Link>
      ) : null}
    </div>
  ) : null;

  const utilityNavItems = currentUser.isAuthenticated ? [
    <Link
      key="utility-mypage"
      className="yowan-shell__nav-link yowan-shell__nav-link--utility-menu"
      to={appendYowanPreviewQueryIfNeeded('/mypage', search)}
      onClick={() => setMenuOpen(false)}
    >
      {adminMailUnreadLabel ? `マイページ（管理メール未読${adminMailUnreadLabel}件）` : 'マイページ'}
    </Link>,
    canAccessAdminMail ? (
      <Link
        key="utility-admin-mail"
        className="yowan-shell__nav-link yowan-shell__nav-link--utility-menu"
        to={appendYowanPreviewQueryIfNeeded('/admin/mail', search)}
        onClick={() => setMenuOpen(false)}
      >
        {adminMailUnreadLabel ? `管理メール（未読${adminMailUnreadLabel}件）` : '管理メール'}
      </Link>
    ) : null,
    canShowStakeholderUtility ? (
      <Link
        key="utility-stakeholder"
        className="yowan-shell__nav-link yowan-shell__nav-link--utility-menu"
        to={appendYowanPreviewQueryIfNeeded('/stakeholder', search)}
        onClick={() => setMenuOpen(false)}
      >
        関係者ページ
      </Link>
    ) : null,
    currentUser.canAccessBlogAuthor ? (
      <Link
        key="utility-blog-author"
        className="yowan-shell__nav-link yowan-shell__nav-link--utility-menu"
        to={appendYowanPreviewQueryIfNeeded('/blog-author', search)}
        onClick={() => setMenuOpen(false)}
      >
        ブログ執筆者ページ
      </Link>
    ) : null,
    currentUser.canAccessAdmin ? (
      <Link
        key="utility-admin"
        className="yowan-shell__nav-link yowan-shell__nav-link--utility-menu"
        to={appendYowanPreviewQueryIfNeeded('/admin', search)}
        onClick={() => setMenuOpen(false)}
      >
        管理画面
      </Link>
    ) : null,
  ].filter(Boolean) : [];

  const menuButton = (
    <button
      type="button"
      className="yowan-shell__menu-button"
      aria-expanded={menuOpen}
      aria-label="ナビゲーションを開閉"
      onPointerDown={(event) => {
        event.stopPropagation();
      }}
      onClick={(event) => {
        event.stopPropagation();
        setMenuOpen((prev) => (canUseHover() && prev ? true : !prev));
      }}
      onMouseEnter={() => {
        if (canUseHover()) {
          setMenuOpen(true);
        }
      }}
    >
      <span className="yowan-shell__menu-button-line" />
      <span className="yowan-shell__menu-button-line" />
      <span className="yowan-shell__menu-button-line" />
    </button>
  );

  return (
    <header className={`yowan-shell__header${isAdminRoute || isStakeholderRoute || isBlogAuthorRoute ? ' yowan-shell__header--admin' : ''}`}>
      {utilityLinks ? <div className="c-nav__cta yowan-shell__header-cta">{utilityLinks}</div> : null}
      <PageContainer className="yowan-shell__header-inner">
        <NavigationBar
          ariaLabel="Yowan primary navigation"
          className={`yowan-shell__nav-shell${shouldForceMenu ? ' yowan-shell__nav-shell--force-menu' : ''}${floatingMenuActive ? ' yowan-shell__nav-shell--floating-menu' : ''}${currentUser.isAuthenticated ? ' yowan-shell__nav-shell--with-utilities' : ''}${menuOpen ? ' yowan-shell__nav-shell--menu-open' : ''}`}
          brand={brand}
          items={[...items, ...utilityNavItems]}
          menuButton={menuButton}
          menuOpen={menuOpen}
          menuRegionProps={{
            ref: menuRegionRef,
            onMouseEnter: () => {
              if (canUseHover()) {
                setMenuOpen(true);
              }
            },
            onMouseLeave: () => {
              if (canUseHover()) {
                setMenuOpen(false);
              }
            },
          }}
        />
      </PageContainer>
    </header>
  );
};

export default YowanHeader;
